import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, patientAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users,
  BedDouble,
  UserCheck,
  FlaskConical,
  Scan,
  Receipt,
  Package,
  TrendingUp,
  UserPlus,
  FileText,
  AlertTriangle,
  CalendarDays,
  Phone,
  Building2,
  BarChart3,
  Brain,
  DoorClosed,
} from 'lucide-react';
import { toast } from 'sonner';
import { ClosedCasesList } from '../components/ClosedCasesList';
import { logger } from '../lib/logger';

const Dashboard = () => {
  const { user, hasRole, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [activePatients, setActivePatients] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patientTab, setPatientTab] = useState('active');
  const [dashboardTab, setDashboardTab] = useState('overview');

  const isDoctor = hasRole('doctor');
  const isNurse = hasRole('nurse');
  const isPlatformAdminUser = isPlatformAdmin && isPlatformAdmin();
  const canViewClosedCases = hasRole(['super_admin', 'admin', 'doctor', 'receptionist']);

  useEffect(() => {
    if (isPlatformAdminUser) {
      fetchPlatformStats();
    } else {
      fetchStats();
      if (isDoctor || isNurse) {
        fetchActivePatients();
        fetchAllPatients();
      }
    }
  }, [isPlatformAdminUser, isDoctor, isNurse]);

  const fetchPlatformStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      logger.debug('Platform admin stats:', response.data);
      setPlatformStats(response.data);
    } catch (error) {
      logger.error('Error loading platform stats:', error);
      toast.error('Failed to load platform statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivePatients = async () => {
    try {
      const response = await patientAPI.getMyPatients();  // include_all=false by default
      setActivePatients(response.data.patients || response.data || []);
    } catch (error) {
      logger.error('Failed to load active patients:', error);
      setActivePatients([]);
    }
  };

  const fetchAllPatients = async () => {
    try {
      // Get all patients with include_all=true parameter - needs custom API call
      const response = await api.get('/dashboard/my-patients', { params: { include_all: true } });
      setAllPatients(response.data.patients || response.data || []);
    } catch (error) {
      logger.error('Failed to load all patients:', error);
      setAllPatients([]);
    }
  };

  // Role-based stat cards
  const getStatCards = () => {
    const baseCards = [
      { label: 'Total Patients', value: stats?.total_patients || 0, icon: Users, color: 'bg-blue-500', roles: ['all'] },
      { label: 'Indoor Patients', value: stats?.indoor_patients || 0, icon: BedDouble, color: 'bg-emerald-500', roles: ['all'] },
      { label: 'Outdoor Patients', value: stats?.outdoor_patients || 0, icon: UserCheck, color: 'bg-violet-500', roles: ['all'] },
      { label: 'Pending Lab Tests', value: stats?.pending_lab_tests || 0, icon: FlaskConical, color: 'bg-amber-500', roles: ['all'] },
      { label: 'Pending Imaging', value: stats?.pending_imaging || 0, icon: Scan, color: 'bg-cyan-500', roles: ['all'] },
    ];

    // Add role-specific cards
    if (hasRole(['super_admin', 'admin', 'receptionist'])) {
      baseCards.push({
        label: 'Pending Bills',
        value: stats?.pending_bills || 0,
        icon: Receipt,
        color: 'bg-red-500',
        alert: stats?.pending_bills > 0,
        roles: ['super_admin', 'admin', 'receptionist']
      });
    }

    if (hasRole(['super_admin', 'admin', 'stockist'])) {
      baseCards.push({
        label: 'Low Stock Items',
        value: stats?.low_stock_items || 0,
        icon: Package,
        color: 'bg-orange-500',
        alert: stats?.low_stock_items > 0,
        roles: ['super_admin', 'admin', 'stockist']
      });
    }

    if (hasRole(['super_admin', 'admin'])) {
      baseCards.push({
        label: 'Total Revenue',
        value: formatCurrency(stats?.total_revenue || 0),
        icon: TrendingUp,
        color: 'bg-green-500',
        isAmount: true,
        roles: ['super_admin', 'admin']
      });
    }

    return baseCards;
  };

  const statCards = getStatCards();

  if (loading) {
    return (
      <div className="page-content flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  // Platform Admin View - Different dashboard for platform administrators
  if (isPlatformAdminUser) {
    // If platform stats not loaded yet, show loading for platform admin too
    if (!platformStats && loading) {
      return (
        <div className="page-content flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-slate-500">Loading platform dashboard...</div>
        </div>
      );
    }
    
    return (
      <div data-testid="platform-admin-dashboard">
        <header className="page-header glass-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Platform Dashboard</h1>
              <p className="text-slate-500">System Administrator - {user?.full_name}</p>
            </div>
          </div>
        </header>

        <div className="page-content">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/platform')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Institutions</p>
                    <p className="text-3xl font-bold text-slate-900">{platformStats.total_institutions || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">Click to manage</p>
                  </div>
                  <Building2 className="w-12 h-12 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/platform/analytics')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Revenue</p>
                    <p className="text-3xl font-bold text-slate-900">₹{platformStats.total_revenue?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">View analytics</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Active Users</p>
                    <p className="text-3xl font-bold text-slate-900">{platformStats.total_users || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">Across all institutions</p>
                  </div>
                  <Users className="w-12 h-12 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Patients</p>
                    <p className="text-3xl font-bold text-slate-900">{platformStats.total_patients || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">System-wide</p>
                  </div>
                  <UserPlus className="w-12 h-12 text-teal-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/platform')}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-500" />
                  Manage Institutions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">View and manage all institutions, subscriptions, and access controls</p>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/platform/analytics')}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Platform Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">Revenue trends, user growth, institution performance metrics</p>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/platform/ml-insights')}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Brain className="w-5 h-5 text-green-500" />
                  ML Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">AI-powered forecasting for revenue and patient volume</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Institution User View - Original detailed dashboard
  return (
    <div data-testid="dashboard-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Dashboard</h1>
            <p className="text-slate-500">Welcome back, {user?.full_name}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="capitalize">{user?.role?.replace('_', ' ')}</span>
            {user?.department && (
              <>
                <span>•</span>
                <span>{user.department}</span>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="page-content space-y-8">
        {/* Dashboard Tabs */}
        {canViewClosedCases ? (
          <Tabs value={dashboardTab} onValueChange={setDashboardTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-sm mb-6" data-testid="dashboard-tabs">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="closed-cases" data-testid="tab-closed-cases">
                <DoorClosed className="w-4 h-4 mr-1" />
                Closed Cases
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <DashboardOverview
                stats={stats} statCards={statCards} isDoctor={isDoctor} isNurse={isNurse}
                activePatients={activePatients} allPatients={allPatients}
                patientTab={patientTab} setPatientTab={setPatientTab} navigate={navigate}
              />
            </TabsContent>

            <TabsContent value="closed-cases">
              <ClosedCasesList />
            </TabsContent>
          </Tabs>
        ) : (
          <DashboardOverview
            stats={stats} statCards={statCards} isDoctor={isDoctor} isNurse={isNurse}
            activePatients={activePatients} allPatients={allPatients}
            patientTab={patientTab} setPatientTab={setPatientTab} navigate={navigate}
          />
        )}
      </div>
    </div>
  );
};

// Patient Card Component
const PatientCard = ({ patient, navigate }) => (
  <div
    className="p-4 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50/30 transition-colors cursor-pointer"
    onClick={() => navigate(`/patients/${patient.id}`)}
  >
    <div className="flex items-start justify-between mb-2">
      <div>
        <p className="font-medium text-slate-900">{patient.full_name}</p>
        <p className="text-xs text-slate-500">{patient.patient_id}</p>
      </div>
      <Badge className={patient.patient_type === 'indoor' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
        {patient.patient_type}
      </Badge>
    </div>
    {patient.specialty && (
      <Badge className="bg-purple-100 text-purple-800 text-xs">{patient.specialty}</Badge>
    )}
    {patient.phone && (
      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
        <Phone className="w-3 h-3" />
        {patient.phone}
      </p>
    )}
    <div className="mt-3 flex gap-2">
      <Button 
        size="sm" variant="outline" className="flex-1 text-xs"
        onClick={(e) => { e.stopPropagation(); navigate(`/prescriptions?patient_id=${patient.id}`); }}
      >
        Prescribe
      </Button>
      <Button 
        size="sm" variant="outline" className="flex-1 text-xs"
        onClick={(e) => { e.stopPropagation(); navigate(`/lab-tests?patient_id=${patient.id}`); }}
      >
        Order Test
      </Button>
    </div>
  </div>
);

// Dashboard Overview sub-component
const DashboardOverview = ({ stats, statCards, isDoctor, isNurse, activePatients, allPatients, patientTab, setPatientTab, navigate }) => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
      {statCards.map((stat, index) => (
        <Card
          key={stat.label}
          className="card-hover animate-fade-in cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          style={{ animationDelay: `${index * 0.05}s` }}
          onClick={() => {
            if (stat.label.includes('Indoor Patients')) navigate('/patients?patient_type=indoor');
            else if (stat.label.includes('Outdoor Patients')) navigate('/patients?patient_type=outdoor');
            else if (stat.label.includes('Total Patients')) navigate('/patients');
            else if (stat.label.includes('Lab')) navigate('/lab-tests?status=pending');
            else if (stat.label.includes('Imaging')) navigate('/imaging-tests?status=pending');
            else if (stat.label.includes('Bill')) navigate('/billing?status=pending');
            else if (stat.label.includes('Stock')) navigate('/stock');
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                <p className={`text-2xl font-bold font-[Manrope] ${stat.alert ? 'text-orange-600' : 'text-slate-900'}`}>
                  {stat.value}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            {stat.alert && (
              <div className="mt-3 flex items-center gap-1 text-orange-600 text-xs">
                <AlertTriangle className="w-3 h-3" />
                <span>Requires attention</span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {(isDoctor || isNurse) && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-red-500" />
              My Patients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={patientTab} onValueChange={setPatientTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-md mb-4">
                <TabsTrigger value="active">Active Patients ({activePatients.length})</TabsTrigger>
                <TabsTrigger value="all">All Patients ({allPatients.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="active">
                {activePatients.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No active patients. Patients you refer will be removed from this list.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activePatients.slice(0, 6).map((patient) => (
                      <PatientCard key={patient.id} patient={patient} navigate={navigate} />
                    ))}
                  </div>
                )}
                {activePatients.length > 6 && (
                  <div className="mt-4 text-center">
                    <Button variant="outline" onClick={() => navigate('/patients')}>View All Active Patients</Button>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="all">
                {allPatients.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No patients assigned yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allPatients.slice(0, 6).map((patient) => (
                      <PatientCard key={patient.id} patient={patient} navigate={navigate} />
                    ))}
                  </div>
                )}
                {allPatients.length > 6 && (
                  <div className="mt-4 text-center">
                    <Button variant="outline" onClick={() => navigate('/patients')}>View All Patients</Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-[Manrope]">Today's Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-slate-600">Active Admissions</span>
              <span className="font-semibold text-slate-900">{stats?.indoor_patients || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-slate-600">Pending Lab Tests</span>
              <span className="font-semibold text-amber-600">{stats?.pending_lab_tests || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-slate-600">Pending Imaging</span>
              <span className="font-semibold text-cyan-600">{stats?.pending_imaging || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600">Outstanding Bills</span>
              <span className="font-semibold text-red-500">{stats?.pending_bills || 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

export default Dashboard;
